import { v1 as uuidv1 } from 'uuid';

import { getMainPrivateApi } from '#/api/apiCoreSdk';
import { getUnlockWalletSignatureTypedData } from '#/api/signatures';
import { signTypedDataWithModal } from '#/lib/ethereum/sign/signHashWithModal';
import { waitAsync } from '#/lib/promiseTools';

import {
  generateDelegatedKey,
  getCompleteDelegatedKey,
} from './delegatedKeyWallet';

import type { DelegatedKeyAuthorization } from '#/reduxState/delegatedKey/authorizationReducer';
import type { IWallet } from '#/reduxState/wallet/walletReducer';

export const requestUnlockAuthSignature = async (
  wallet: IWallet,
  enableDelegatedKey: boolean
) => {
  const { walletAddress } = wallet;
  const nonce = uuidv1();
  const delegatedKeyParams = enableDelegatedKey
    ? await generateDelegatedKey(nonce)
    : undefined;
  const typedDataToSign = await getUnlockWalletSignatureTypedData({
    nonce,
    delegatedKey: delegatedKeyParams?.delegatedKey,
  });
  const signature = await signTypedDataWithModal(
    wallet,
    undefined,
    typedDataToSign
  );
  console.log('[assignWallet] Sign', {
    walletAddress,
    signature,
    delegatedKeyParams,
    enableDelegatedKey,
    messageToSign: typedDataToSign,
  });
  return { signature, nonce, delegatedKeyParams };
};

export const associateWallet = async (
  custodyWallet: IWallet,
  enableDelegatedKey: boolean,
  unlockAuth: Awaited<ReturnType<typeof requestUnlockAuthSignature>>,
  promotions?: {
    referralCode?: string;
    welcomeBonusCode?: string;
  }
) => {
  const { delegatedKeyParams, nonce, signature } = unlockAuth;

  await getMainPrivateApi().internalAssociateWallet({
    ...delegatedKeyParams,
    walletAddress: custodyWallet.walletAddress,
    walletType: custodyWallet.walletType || 'unknown',
    referralCode: promotions?.referralCode,
    welcomeBonusCode: promotions?.welcomeBonusCode,
    nonce,
    signature,
  });

  // Wait a bit, to make sure wallet is already associated in the API - it's required for next request
  // NOTE: should be needed nownot , reducing duration
  // await waitAsync(500);
  await waitAsync(100);

  const { personalWalletSecret } = await getMainPrivateApi().getWalletGuard(
    custodyWallet.walletAddress
  );

  if (!enableDelegatedKey) {
    return { personalWalletSecret, delegatedKey: undefined } as const;
  }
  if (!delegatedKeyParams?.delegatedHDWallet) {
    throw new Error('Error: Missing delegatedHDWallet');
  }
  const delegatedKey = await getCompleteDelegatedKey({
    custodyWalletAddress: custodyWallet.walletAddress,
    delegatedWalletHD: delegatedKeyParams.delegatedHDWallet,
    personalWalletSecret,
  });
  return {
    personalWalletSecret,
    delegatedKey,
  } as const;
};

/** Returns the personalWalletSecret and delegatedKeyAuthorization */
export const signAndAssociateWalletWithModal = async (
  wallet: IWallet,
  enableDelegatedKey: boolean,
  promotions?: {
    referralCode?: string;
    welcomeBonusCode?: string;
  }
): Promise<{
  personalWalletSecret: string;
  delegatedKey?: DelegatedKeyAuthorization;
  // appliedPromotions?: { didRedeemReferralCode?: boolean; welcomeBonusSuccessMessage?: string | null; };
}> => {
  const unlockAuth = await requestUnlockAuthSignature(
    wallet,
    enableDelegatedKey
  );
  const { personalWalletSecret, delegatedKey } = await associateWallet(
    wallet,
    enableDelegatedKey,
    unlockAuth,
    promotions
  );
  return { personalWalletSecret, delegatedKey };
};
